import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  HandlersProjectDeliverableTypeResponse,
  useLazyGetApiProjectsDeliverableTypesQuery,
} from '../../app/athenaApiGen';

type TProjectDeliverableTypeFilterProps = {
  handleChange: (id?: string | string[] | null) => void;
  required?: boolean;
  deliverableTypeId?: string | string[];
  blacklistTypeIds?: string[];
  multiple?: boolean;
};

const ProjectDeliverableTypeFilter = (
  props: TProjectDeliverableTypeFilterProps,
) => {
  const [open, setOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<
    HandlersProjectDeliverableTypeResponse[]
  >([]);
  const [queryTypes, { isLoading }] =
    useLazyGetApiProjectsDeliverableTypesQuery();

  const handleOpen = async () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    const seen = new Set<string>();

    (async () => {
      try {
        const types = await queryTypes({}).unwrap();
        const dupesRemoved = types.filter((item) => {
          // Filter out items without deliverableTypeId
          if (!item.deliverableTypeId) return false;
          // Filter out duplicates (keep first occurrence)
          if (seen.has(item.deliverableTypeId)) return false;
          seen.add(item.deliverableTypeId);
          return true;
        });
        if (
          (props.blacklistTypeIds?.length ?? 0) > 0 &&
          dupesRemoved.length > 0
        ) {
          setOptions(
            dupesRemoved.filter(
              (type) =>
                !props.blacklistTypeIds?.includes(type.deliverableTypeId ?? ''),
            ),
          );
        } else {
          setOptions(dupesRemoved);
        }
      } catch (err) {
        console.log(err);
      }
    })();
  }, [queryTypes, props.blacklistTypeIds]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <Autocomplete
        disableCloseOnSelect={props.multiple}
        open={open}
        onOpen={handleOpen}
        multiple={props.multiple}
        onClose={handleClose}
        filterSelectedOptions={props.multiple}
        isOptionEqualToValue={(option, value) =>
          (option as HandlersProjectDeliverableTypeResponse)?.deliverableType
            ?.title ===
          (value as HandlersProjectDeliverableTypeResponse)?.deliverableType
            ?.title
        }
        getOptionLabel={(option) =>
          (option as HandlersProjectDeliverableTypeResponse)?.deliverableType
            ?.title ?? ''
        }
        options={options}
        loading={isLoading}
        value={
          !props.multiple
            ? options.find(
                (opt) => opt.deliverableTypeId === props.deliverableTypeId,
              )
            : (props.deliverableTypeId as string[]).map((id) =>
                options.find((opt) => opt.deliverableTypeId === id),
              )
        }
        onChange={(_, newValue) => {
          if (
            _.type === 'click' &&
            ((newValue as HandlersProjectDeliverableTypeResponse[])?.length ??
              0) < 1
          ) {
            props.handleChange([]);
          }
          if (
            !newValue ||
            ((newValue as HandlersProjectDeliverableTypeResponse[])?.length ??
              0) < 1
          )
            return;

          if (!props.multiple) {
            props.handleChange(
              newValue
                ? (newValue as HandlersProjectDeliverableTypeResponse)
                    ?.deliverableTypeId
                : null,
            );
          } else {
            const arrType: HandlersProjectDeliverableTypeResponse[] =
              newValue as HandlersProjectDeliverableTypeResponse[];
            const types: string[] = arrType?.map(
              (del) => del.deliverableTypeId ?? '',
            );
            props.handleChange(types);
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            required={props.required}
            label="Type"
            size="small"
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              },
            }}
          />
        )}
      />
    </Box>
  );
};

export default ProjectDeliverableTypeFilter;
